/**
 * Unified Premium-feature gate hook.
 *
 * Every Premium-locked action (Lost Mode activation, PawLink Rescue Map,
 * rescue-alert fan-out, live GPS tracking, extra pets) should call the
 * function returned by this hook BEFORE doing its work. If it returns
 * true, the caller must abort — the hook has already surfaced the
 * appropriate upsell for the current platform:
 *
 *   • Real Premium user (any platform) → NO gate, function returns false.
 *   • Free user on store-app (Google Play Android)
 *       → returns true, opens the "Premium is coming soon 🐾" modal
 *         (no purchase surface, no external link, no fake urgency).
 *   • Free user on web (getpawlink.com)
 *       → returns true, redirects to `/subscription` — the existing
 *         legitimate Paddle web upgrade path is preserved. If the user
 *         is not authenticated yet, they land on the auth-protected
 *         route and are asked to sign in first, keeping the existing
 *         architecture.
 *
 * This is a UI-side hook — no backend gates are modified. Server-side
 * enforcement (e.g. the FREE_PET_LIMIT 402 on POST /pets) still executes;
 * the hook only handles the UI response so free users never see a raw
 * error, a broken checkout, or an unintended unlock.
 *
 * Usage:
 *
 *     const openPremiumGate = useHandlePremiumGate(user);
 *     const onArm = () => {
 *         if (openPremiumGate("lost_mode")) return;
 *         setConfirmOpen(true);
 *     };
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useIsStoreApp } from "@/lib/storeAppDetection";
import { useShowPremiumComingSoon } from "@/context/PremiumComingSoonContext";
import { track } from "@/lib/analytics";

export function useHandlePremiumGate(user) {
    const isStoreApp = useIsStoreApp();
    const isRealPremium = user?.plan === "premium";
    const showComingSoon = useShowPremiumComingSoon();
    const nav = useNavigate();

    return useCallback((feature = "premium") => {
        if (isRealPremium) return false;                        // no gate
        if (isStoreApp) {
            showComingSoon(feature);                            // Google Play: modal only
        } else {
            // Web: route to the existing Paddle upgrade path.
            track("premium_gate_web_redirect", { feature });
            nav("/subscription");
        }
        return true;
    }, [isRealPremium, isStoreApp, showComingSoon, nav]);
}
